// ============================================================================
// /subcontractors/directory — Subcontractor Directory: find / analyze /
// compare all subs over v_subcontractor_scorecard. Per-module search
// (decision 6/10: facilities and subs each search inside their module; the
// AI is the universal cross-entity resolver). Typeahead matches canonical
// names AND entity_aliases (owner / legal / POC names — "Serena" resolves).
// Visual flags: low margin (<10%) and high open feedback (top decile).
// Row → /subcontractors/:providerId (sub card).
// ============================================================================
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowDown, ArrowUp, Check, Search, ShieldAlert, X } from 'lucide-react';
import { PageHeader, EmptyState, ErrorState, Skeleton } from '../../shared/components';
import { coiCovered, w9OnFile } from '../../shared/coi-state';
import {
  fmtMoney,
  fmtPct,
  useSubDirectory,
  type SubScorecardRow,
} from './hooks/use-sub-data';

type SortKey =
  | 'partner_name'
  | 'market_region'
  | 'active_facilities'
  | 'monthly_client_billing'
  | 'monthly_sub_cost'
  | 'monthly_gross_margin'
  | 'margin_pct'
  | 'feedback_total'
  | 'negative_fb'
  | 'open_fb';

type Filter = 'all' | 'active' | 'compliance_gap' | 'low_margin' | 'high_open_fb';

const LOW_MARGIN_PCT = 10;

export default function SubDirectory() {
  const navigate = useNavigate();
  const { rows, aliases, nameFields, loading, error, refetch } = useSubDirectory();
  const [params, setParams] = useSearchParams();
  const filter = (params.get('filter') as Filter) || 'active';
  const [q, setQ] = useState('');
  // 2026-07-06 — debounce the raw input ~200ms; the filter itself stays
  // client-side (~100 providers, one fetch).
  const [debouncedQ, setDebouncedQ] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q), 200);
    return () => clearTimeout(t);
  }, [q]);
  const [sortKey, setSortKey] = useState<SortKey>('active_facilities');
  const [sortAsc, setSortAsc] = useState(false);

  // alias map: provider_id -> aliases (for search + "via alias" caption)
  const aliasMap = useMemo(() => {
    const m = new Map<string, string[]>();
    for (const a of aliases) {
      const list = m.get(a.entity_id) ?? [];
      list.push(a.alias);
      m.set(a.entity_id, list);
    }
    return m;
  }, [aliases]);

  // Top-decile open-feedback threshold across subs with any open feedback.
  const openFbThreshold = useMemo(() => {
    const counts = rows.map((r) => r.open_fb).filter((n) => n > 0).sort((a, b) => a - b);
    if (counts.length === 0) return Infinity;
    const idx = Math.max(0, Math.ceil(counts.length * 0.9) - 1);
    return Math.max(counts[idx], 10); // never flag tiny counts
  }, [rows]);

  const searched = useMemo(() => {
    const norm = debouncedQ.trim().toLowerCase();
    if (!norm) return rows.map((r) => ({ row: r, via: null as string | null }));
    return rows
      .map((r) => {
        if (r.partner_name.toLowerCase().includes(norm)) {
          return { row: r, via: null as string | null };
        }
        // 2026-07-06 — owner / legal / POC name fields match directly (not
        // only via a minted alias). "matched" caption shows what hit.
        const nf = nameFields.get(r.provider_id);
        const fieldHit = [
          nf?.owner_name,
          nf?.legal_business_name,
          nf?.primary_poc_name,
          nf?.secondary_poc_name,
        ].find((v) => v && v.toLowerCase().includes(norm));
        if (fieldHit) return { row: r, via: fieldHit };
        const hit = (aliasMap.get(r.provider_id) ?? []).find((a) =>
          a.toLowerCase().includes(norm),
        );
        return hit ? { row: r, via: hit } : null;
      })
      .filter((x): x is { row: SubScorecardRow; via: string | null } => x !== null);
  }, [rows, aliasMap, nameFields, debouncedQ]);

  const filtered = useMemo(() => {
    switch (filter) {
      case 'active':
        return searched.filter(({ row }) => row.provider_status === 'Active');
      case 'compliance_gap':
        // Compare to the NAMED STATE, never truthiness. Until 2026-08-24 this
        // read `!row.w9_status || !row.coi_status` against a column holding the
        // words 'Yes' / 'No' — so it matched only the 4 subs whose value was
        // NULL and missed all 43 recorded as uninsured.
        return searched.filter(
          ({ row }) => !w9OnFile(row.w9_status) || !coiCovered(row.coi_status),
        );
      case 'low_margin':
        return searched.filter(
          ({ row }) =>
            row.flat_facilities > 0 &&
            row.margin_pct != null &&
            Number(row.margin_pct) < LOW_MARGIN_PCT,
        );
      case 'high_open_fb':
        return searched.filter(({ row }) => row.open_fb >= openFbThreshold);
      default:
        return searched;
    }
  }, [searched, filter, openFbThreshold]);

  const sorted = useMemo(() => {
    const dir = sortAsc ? 1 : -1;
    return [...filtered].sort((a, b) => {
      const av = a.row[sortKey];
      const bv = b.row[sortKey];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === 'string' && typeof bv === 'string') {
        return av.localeCompare(bv) * dir;
      }
      return (Number(av) - Number(bv)) * dir;
    });
  }, [filtered, sortKey, sortAsc]);

  function setFilter(f: Filter) {
    const next = new URLSearchParams(params);
    f === 'active' ? next.delete('filter') : next.set('filter', f);
    setParams(next, { replace: true });
  }

  function toggleSort(k: SortKey, defaultAsc = false) {
    if (sortKey === k) {
      setSortAsc((v) => !v);
    } else {
      setSortKey(k);
      setSortAsc(defaultAsc);
    }
  }

  if (error) return <ErrorState body={error} onRetry={refetch} />;

  return (
    <div className="mx-auto max-w-7xl space-y-4 p-4 md:p-6">
      <PageHeader
        eyebrow="Subcontractors / Partners"
        title="Subcontractor directory"
        meta={`${sorted.length} of ${rows.length} subs · revenue, cost, margin & feedback per sub`}
        actions={
          <Link
            to="/subcontractors"
            className="rounded-md border border-tcs-border bg-tcs-raised px-3 py-1.5 text-sm text-tcs-text hover:bg-tcs-surface"
          >
            Compliance &amp; onboarding
          </Link>
        }
      />

      {/* Search — canonical names + aliases (owner / legal / POC) */}
      <div className="relative max-w-md">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-tcs-text-muted" />
        <input
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search subs by name, owner, legal name, or POC…"
          className="block w-full rounded-md border border-tcs-border bg-tcs-field py-2 pl-9 pr-3 text-sm text-tcs-text placeholder:text-tcs-text-muted focus:border-tcs-border-input focus:outline-none"
        />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <FilterPill label="Active" active={filter === 'active'} onClick={() => setFilter('active')} />
        <FilterPill label="All statuses" active={filter === 'all'} onClick={() => setFilter('all')} />
        <FilterPill
          label="Compliance gap (W9/COI)"
          active={filter === 'compliance_gap'}
          onClick={() => setFilter('compliance_gap')}
        />
        <FilterPill
          label={`Low margin (<${LOW_MARGIN_PCT}%)`}
          active={filter === 'low_margin'}
          onClick={() => setFilter('low_margin')}
        />
        <FilterPill
          label="High open feedback"
          active={filter === 'high_open_fb'}
          onClick={() => setFilter('high_open_fb')}
        />
      </div>

      {loading ? (
        <div className="space-y-2">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      ) : sorted.length === 0 ? (
        <EmptyState
          title="No subs match"
          body="Try a different search or filter — aliases (owner, legal, POC names) are searched too."
        />
      ) : (
        <div className="overflow-x-auto rounded-lg border border-tcs-border bg-tcs-raised">
          <table className="min-w-full divide-y divide-tcs-border text-sm">
            <thead>
              <tr className="text-left text-[11px] font-semibold uppercase tracking-wide text-tcs-text-muted">
                <Th label="Sub" k="partner_name" sortKey={sortKey} asc={sortAsc} onSort={() => toggleSort('partner_name', true)} />
                <Th label="Market" k="market_region" sortKey={sortKey} asc={sortAsc} onSort={() => toggleSort('market_region', true)} />
                <Th label="Facilities" k="active_facilities" sortKey={sortKey} asc={sortAsc} onSort={() => toggleSort('active_facilities')} right />
                <Th label="Billing /mo" k="monthly_client_billing" sortKey={sortKey} asc={sortAsc} onSort={() => toggleSort('monthly_client_billing')} right />
                <Th label="Sub cost /mo" k="monthly_sub_cost" sortKey={sortKey} asc={sortAsc} onSort={() => toggleSort('monthly_sub_cost')} right />
                <Th label="Margin /mo" k="monthly_gross_margin" sortKey={sortKey} asc={sortAsc} onSort={() => toggleSort('monthly_gross_margin')} right />
                <Th label="Margin %" k="margin_pct" sortKey={sortKey} asc={sortAsc} onSort={() => toggleSort('margin_pct')} right />
                <Th label="Feedback" k="feedback_total" sortKey={sortKey} asc={sortAsc} onSort={() => toggleSort('feedback_total')} right />
                <Th label="Open" k="open_fb" sortKey={sortKey} asc={sortAsc} onSort={() => toggleSort('open_fb')} right />
                <th className="px-3 py-2">W9</th>
                <th className="px-3 py-2">COI</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-tcs-border">
              {sorted.map(({ row: r, via }) => {
                const lowMargin =
                  r.flat_facilities > 0 &&
                  r.margin_pct != null &&
                  Number(r.margin_pct) < LOW_MARGIN_PCT;
                const highOpen = r.open_fb >= openFbThreshold;
                return (
                  <tr
                    key={r.provider_id}
                    onClick={() => navigate(`/subcontractors/${r.provider_id}`)}
                    className="cursor-pointer hover:bg-tcs-surface"
                  >
                    <td className="px-3 py-2">
                      <div className="font-medium text-tcs-text">{r.partner_name}</div>
                      <div className="text-xs text-tcs-text-muted">
                        <span className="font-mono">{r.provider_id}</span>
                        {r.provider_status && r.provider_status !== 'Active' && (
                          <span className="ml-2 rounded bg-tcs-surface px-1.5 py-0.5">{r.provider_status}</span>
                        )}
                        {via && <span className="ml-2 italic">matched alias “{via}”</span>}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-tcs-text-secondary">{r.market_region ?? '—'}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{r.active_facilities}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmtMoney(r.monthly_client_billing)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmtMoney(r.monthly_sub_cost)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmtMoney(r.monthly_gross_margin)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      <span
                        title={
                          r.variable_facilities > 0
                            ? `Flat-billed work only — ${r.variable_facilities} hourly/variable ${
                                r.variable_facilities === 1 ? 'facility is' : 'facilities are'
                              } excluded from margin (recorded cost ${fmtMoney(r.variable_sub_cost)}/mo)`
                            : undefined
                        }
                        className={
                          lowMargin
                            ? 'rounded bg-st-danger-bg px-1.5 py-0.5 font-medium text-st-danger-fg'
                            : undefined
                        }
                      >
                        {r.flat_facilities > 0 ? fmtPct(r.margin_pct) : '—'}
                        {r.variable_facilities > 0 && (
                          <span className="ml-1 text-[10px] text-tcs-text-muted">
                            +{r.variable_facilities} hr
                          </span>
                        )}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">{r.feedback_total}</td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      <span
                        className={
                          highOpen
                            ? 'rounded bg-st-warn-bg px-1.5 py-0.5 font-medium text-st-warn-fg-strong'
                            : undefined
                        }
                      >
                        {r.open_fb}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <ComplianceMark ok={w9OnFile(r.w9_status)} state={r.w9_status} />
                    </td>
                    <td className="px-3 py-2">
                      <ComplianceMark ok={coiCovered(r.coi_status)} state={r.coi_status} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {filter === 'compliance_gap' && sorted.length > 0 && (
        <div className="flex items-center gap-2 rounded-md border border-st-warn-border bg-st-warn-bg p-3 text-sm text-st-warn-fg-strong">
          <ShieldAlert className="h-4 w-4 shrink-0" />
          <span>
            These subs are missing a W9 or COI. Work the list in{' '}
            <Link to="/subcontractors?filter=coi_attention" className="underline">
              Compliance &amp; onboarding
            </Link>{' '}
            (edit, send reminder, upload docs) — that flow already feeds partner documents and HR.
          </span>
        </div>
      )}
    </div>
  );
}

function Th({
  label,
  k,
  sortKey,
  asc,
  onSort,
  right,
}: {
  label: string;
  k: SortKey;
  sortKey: SortKey;
  asc: boolean;
  onSort: () => void;
  right?: boolean;
}) {
  const active = sortKey === k;
  return (
    <th
      onClick={onSort}
      className={`cursor-pointer select-none px-3 py-2 hover:text-tcs-text ${right ? 'text-right' : ''}`}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {active && (asc ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />)}
      </span>
    </th>
  );
}

// The tick and the WORD, together. `ok` is decided by a state comparison
// (never truthiness); `state` is the word the database published, so "no
// certificate ever uploaded" is distinguishable from "the one on file lapsed".
function ComplianceMark({ ok, state }: { ok: boolean; state: string | null }) {
  const text = state ?? 'unknown';
  return (
    <span className="inline-flex items-center gap-1 whitespace-nowrap" title={text}>
      {ok ? (
        <Check className="h-4 w-4 shrink-0 text-st-ok-fg" aria-label={text} />
      ) : (
        <X className="h-4 w-4 shrink-0 text-st-danger-fg" aria-label={text} />
      )}
      <span className="text-[11px] text-tcs-text-muted">{text}</span>
    </span>
  );
}

function FilterPill({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-full border px-3 py-1 text-xs font-medium ${
        active
          ? 'border-tcs-inverse bg-tcs-inverse text-tcs-inverse-ink'
          : 'border-tcs-border bg-tcs-raised text-tcs-text-secondary hover:bg-tcs-surface'
      }`}
    >
      {label}
    </button>
  );
}
